'use client';

import { Check } from 'lucide-react';
import { cn } from '@/libs/tailwind';

type Section = {
  steps: number;
  label?: string;
};

export type ProgressBarProps = {
  currentStep: number;
  totalSteps?: number;
  sections?: Section[];
  className?: string;
  color?: string;
};

export function ProgressBar({
  currentStep,
  totalSteps,
  sections,
  className,
  color = 'bg-blue-40',
}: ProgressBarProps) {
  // Check if we should use sections or simple progress mode
  const useSimpleProgress = !sections && totalSteps !== undefined;

  // Render a simple progress bar if no sections are provided
  if (useSimpleProgress) {
    const progressPercentage = `${Math.min(100, Math.max(0, (currentStep / totalSteps) * 100))}%`;

    return (
      <div className={cn('w-full', className)}>
        <div className="h-[4px] w-full bg-[#E7E9F7]">
          <div
            className={`h-full ${color} transition-all duration-500 ease-in-out`}
            style={{ width: progressPercentage }}
          />
        </div>
      </div>
    );
  }

  // Otherwise, render sectioned progress bar
  return (
    <div className={cn('w-full', className)}>
      <div className="relative flex items-start">
        {sections?.map((section, index) => {
          // Calculate the cumulative progress for each section
          const cumulativeSteps = sections.slice(0, index).reduce((acc, val) => acc + val.steps, 0);
          const sectionProgress
            = currentStep >= cumulativeSteps + section.steps
              ? '100%' // Section fully completed
              : currentStep >= cumulativeSteps
                ? `${((currentStep - cumulativeSteps) / section.steps) * 100}%` // Partial progress in the current section
                : '0%'; // Section not started yet

          // Determine if this section's title should be bold
          const isActive = currentStep >= cumulativeSteps && currentStep < cumulativeSteps + section.steps;
          const isCompleted = currentStep >= cumulativeSteps + section.steps; // Check if the section is completed

          const textColor = isActive
            ? 'text-[#14151C]'
            : 'text-[#878AAE]';

          return (
            <div key={`section-${index}`} className="flex-1">
              <div className="h-[6px] w-full bg-[#E7E9F7]">
                <div
                  className="h-full bg-black transition-all duration-500 ease-in-out"
                  style={{ width: sectionProgress }}
                />
              </div>
              {section.label && (
                <div
                  className={`mt-[12px] text-center text-sm ${isActive ? 'font-bold' : 'font-normal'} ${textColor} flex items-center justify-center`}
                >
                  {/* Render the icon if the section is complete */}
                  {isCompleted
                    ? (
                        <Check size={18} color="#606384" className="inline-block" />
                      )
                    : `${index + 1}. `}
                  {section.label}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
